import { useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BORNES_PROFIL, ENERGIES_DU_PROFIL, type ChampDuProfil, type FeuilleDeProfil } from '@nextilt/commun';
import type { ContactDuProfil, LigneDeProfil, ReponseProfil } from '../partage/api/types';
import { useCadreDuHaut } from '../partage/cadreDuHaut';
import { FeuilleDuBas } from '../partage/FeuilleDuBas';
import { HautDuPouce } from '../partage/HautDuPouce';
import { IconeCombine } from '../partage/icones';
import { Curseur } from '../partage/PiecesDuCarnet';
import {
  AJOUTER_UNE_INFO,
  ANNULER,
  CLAUDE_LE_LIT,
  CONTACTS_RELUS,
  CRANS_DE_LA_VOITURE,
  CRANS_DU_PERMIS,
  DESSUS_DU_PROFIL_AU_POUCE,
  ENREGISTRER_LE_PROFIL,
  EXEMPLES_DES_CHAMPS_DU_PROFIL,
  EXPLICATION_DU_SENSIBLE_AU_POUCE,
  FEUILLE_MES_CONTACTS,
  FEUILLE_PROPOSE_PAR_CLAUDE,
  FEUILLE_SENSIBLE,
  FERME_POUR_L_INSTANT,
  GARDER_LA_PROPOSITION,
  INTITULE_DE_L_INFO,
  MON_PROFIL,
  MOTS_DES_CHAMPS_DU_PROFIL,
  PARAMETRES,
  PAS_DE_CONTACT,
  PAS_DE_PROPOSITION,
  PROPOSITIONS_DE_CLAUDE,
  REFUSER_LA_PROPOSITION,
  RETIRER_L_INFO,
  SENSIBLE_FERME,
  TITRES_DES_FEUILLES_DU_PROFIL,
  TOI,
  UNE_INFO_A_TOI,
  VALEUR_DE_L_INFO,
  aTrier,
  cheminDansLeProfil,
  corriger,
  ditDeLaProposition,
  origineDeLaProposition,
  origineDuContact,
  remplisSur,
} from '../partage/vocabulaire';
import {
  CHAMPS_DE_LA_FEUILLE,
  INTERCALAIRES_DU_PROFIL,
  compteDeLaFeuille,
  initialeDuProfil,
  nomDuContact,
  valeurHorsDesCrans,
  type FeuilleDeLaPage,
} from './reglesDuProfil';
import { useProfil } from './useProfil';
import './ProfilAuPouce.css';

/**
 * Le Profil au téléphone (chantier telephone-profil, 01, maquette `app-au-telephone.html`,
 * `profil()`, `sectionProfil()`, `champLigne`, `feuilleHtml()` branche `champ:`) : ouvert par
 * « Mon profil » des Paramètres, ou par son adresse. En haut le retour, la carte (l'initiale en
 * grand, le prénom, la ville et ce que tu fais) ; dessous six intercalaires qu'un toucher déplie.
 *
 * Les mêmes règles et les mêmes gestes que la page de l'ordinateur (`useProfil`,
 * `reglesDuProfil.ts`) : une ligne touchée fait monter une feuille qui la corrige ; le permis, la
 * voiture et l'énergie en curseurs, dont le mot choisi s'écrit dans le champ (un second toucher
 * sur le cran choisi le retire, comme l'énergie à l'ordinateur).
 *
 * Couché (02, maquette `cProfil()`) : la même page en deux colonnes, la carte et Qui je suis à
 * gauche, le reste à droite, tout déplié ; la même feuille qui corrige, sur le côté.
 *
 * Ce qui est déplié, et la feuille ouverte, sont l'état de la page, oubliés en la quittant.
 */
export function ProfilAuPouce({ couche = false }: { couche?: boolean }) {
  const profil = useProfil();
  const retour = useRetourAuxParametres();
  const [ouverts, setOuverts] = useState<ReadonlySet<FeuilleDeLaPage>>(() => new Set());
  const [feuille, setFeuille] = useState<FeuilleOuverte | null>(null);
  const lu = profil.lecture;

  const basculer = (f: FeuilleDeLaPage) =>
    setOuverts((avant) => {
      const apres = new Set(avant);
      if (apres.has(f)) apres.delete(f);
      else apres.add(f);
      return apres;
    });

  const intercalaire = (f: FeuilleDeLaPage) => {
    // Couché, tout est déplié, sans toucher pour déplier (`cProfil()`, `.pf-inter.ouvert`).
    const ouvert = couche || ouverts.has(f);
    const classe = `profil-pouce-inter${f === 'sensible' ? ' profil-pouce-inter--gris' : ''}${ouvert ? ' est-ouvert' : ''}`;
    return (
      lu && (
        <div key={f} className="profil-pouce-feuille" data-feuille={f}>
          {couche ? (
            <h2 className={classe}>
              <span aria-hidden="true" />
              {TITRES_DES_INTERCALAIRES[f]}
            </h2>
          ) : (
            <button type="button" className={classe} aria-expanded={ouvert} onClick={() => basculer(f)}>
              <span aria-hidden="true">{ouvert ? '▾' : '▸'}</span>
              {TITRES_DES_INTERCALAIRES[f]}
              <small>{compteDeLIntercalaire(lu, f)}</small>
            </button>
          )}
          {ouvert && (
            <div className="profil-pouce-paquet">
              <PaquetDuProfil feuille={f} lu={lu} profil={profil} ouvrir={setFeuille} />
            </div>
          )}
        </div>
      )
    );
  };

  const annonce = profil.annonce && (
    <p className="profil-pouce-annonce" role="status">
      {profil.annonce}
    </p>
  );

  return (
    <section className={`profil-pouce${couche ? ' profil-pouce--couche' : ''}`} aria-label={MON_PROFIL}>
      {couche ? (
        <HautDuPouce titre={MON_PROFIL} retour={retour} couche />
      ) : (
        <HautDuPouce titre={MON_PROFIL} dessus={<span>{DESSUS_DU_PROFIL_AU_POUCE}</span>} retour={retour} />
      )}
      {annonce}
      {lu &&
        (couche ? (
          // Couché (02) : la carte et Qui je suis à gauche, le reste à droite ; chaque colonne défile seule.
          <div className="profil-pouce-colonnes">
            <div className="profil-pouce-colonne">
              <CarteDuProfil lu={lu} />
              {INTERCALAIRES_DU_PROFIL.slice(0, 1).map(intercalaire)}
            </div>
            <div className="profil-pouce-colonne">{INTERCALAIRES_DU_PROFIL.slice(1).map(intercalaire)}</div>
          </div>
        ) : (
          <div className="profil-pouce-corps">
            <CarteDuProfil lu={lu} />
            {INTERCALAIRES_DU_PROFIL.map(intercalaire)}
          </div>
        ))}

      {feuille && lu && <FeuilleDeCorrection ouverte={feuille} profil={profil} onFermer={() => setFeuille(null)} />}
    </section>
  );
}

type Gestes = ReturnType<typeof useProfil>;

/** La feuille qui monte : un champ, une ligne libre, ou une ligne neuve d'une feuille. */
type FeuilleOuverte =
  | { sorte: 'champ'; champ: ChampDuProfil; feuille: FeuilleDeProfil; valeur: string | null }
  | { sorte: 'ligne'; ligne: LigneDeProfil }
  | { sorte: 'neuve'; feuille: FeuilleDeProfil };

const TITRES_DES_INTERCALAIRES: Record<FeuilleDeLaPage, string> = {
  ...TITRES_DES_FEUILLES_DU_PROFIL,
  contacts: FEUILLE_MES_CONTACTS,
  propose: FEUILLE_PROPOSE_PAR_CLAUDE,
  sensible: FEUILLE_SENSIBLE,
};

/** Les champs en curseur, et leurs crans. */
const CRANS_DES_CHAMPS: Partial<Record<ChampDuProfil, readonly string[]>> = {
  permis: CRANS_DU_PERMIS,
  voiture: CRANS_DE_LA_VOITURE,
  courage: ENERGIES_DU_PROFIL,
};

/** Le compte à droite de l'intercalaire (`.pf-inter small`). */
function compteDeLIntercalaire(lu: ReponseProfil, f: FeuilleDeLaPage): string {
  if (f === 'sensible') return SENSIBLE_FERME;
  if (f === 'propose') return aTrier(lu.propositions.length);
  if (f === 'contacts') return String(lu.contacts.length);
  const { remplis, total } = compteDeLaFeuille(lu, f);
  return f === 'qui-je-suis' ? remplisSur(remplis, total) : String(remplis);
}

/**
 * Le retour du haut : vers les Paramètres, par-dessus l'écran d'où l'on est venu (« Mon profil »
 * le donne en ouvrant la page), sinon Maintenant ; le Profil ne reste pas dans l'historique.
 */
function useRetourAuxParametres() {
  const cadre = useCadreDuHaut();
  const naviguer = useNavigate();
  const ici = useLocation();
  const depuis = (ici.state as { depuis?: string } | null)?.depuis;
  return {
    nom: PARAMETRES,
    onRetour: () => {
      naviguer(depuis ?? '/maintenant', { replace: true });
      cadre?.ouvrirLesParametres();
    },
  };
}

/** La carte du haut (`.pid`) : l'initiale en grand, le prénom, la ville et ce que tu fais. */
function CarteDuProfil({ lu }: { lu: ReponseProfil }) {
  const { prenom, ville, situation } = lu.profil;
  const dessous = [ville, situation].filter((x): x is string => x !== null).join(' · ');
  return (
    <div className="profil-pouce-carte">
      <span className="profil-pouce-initiale" aria-hidden="true">
        {initialeDuProfil(prenom)}
      </span>
      <div className="profil-pouce-qui">
        <b>{prenom ?? TOI}</b>
        {dessous && <small>{dessous}</small>}
      </div>
    </div>
  );
}

/** Ce qu'un intercalaire déplie, selon sa feuille (`sectionProfil()`). */
function PaquetDuProfil({
  feuille,
  lu,
  profil,
  ouvrir,
}: {
  feuille: FeuilleDeLaPage;
  lu: ReponseProfil;
  profil: Gestes;
  ouvrir: (f: FeuilleOuverte) => void;
}) {
  if (feuille === 'sensible') {
    return (
      <div className="profil-pouce-ferme">
        <span aria-hidden="true">🔒</span>
        <div>
          <b>{FERME_POUR_L_INSTANT}</b>
          <small>{EXPLICATION_DU_SENSIBLE_AU_POUCE}</small>
        </div>
      </div>
    );
  }
  if (feuille === 'contacts') {
    return (
      <>
        <p className="profil-pouce-sous">{CONTACTS_RELUS}</p>
        {lu.contacts.length === 0 ? (
          <p className="profil-pouce-vide">{PAS_DE_CONTACT}</p>
        ) : (
          lu.contacts.map((c) => <LigneDeContact key={`${c.bulle.id}:${c.telephone ?? ''}:${c.email ?? ''}`} contact={c} />)
        )}
      </>
    );
  }
  if (feuille === 'propose') {
    return (
      <>
        <p className="profil-pouce-sous">{PROPOSITIONS_DE_CLAUDE}</p>
        {lu.propositions.length === 0 ? (
          <p className="profil-pouce-vide">{PAS_DE_PROPOSITION}</p>
        ) : (
          lu.propositions.map((p) => (
            <div key={p.id} className="profil-pouce-prop" data-proposition={p.id}>
              <div>
                <b>{ditDeLaProposition(p)}</b>
                <small>{origineDeLaProposition(p)}</small>
              </div>
              <div className="profil-pouce-prop-gestes">
                <button type="button" onClick={() => profil.garderUneProposition(p)}>
                  {GARDER_LA_PROPOSITION}
                </button>
                <button type="button" className="profil-pouce-non" onClick={() => profil.refuserUneProposition(p)}>
                  {REFUSER_LA_PROPOSITION}
                </button>
              </div>
            </div>
          ))
        )}
      </>
    );
  }

  return (
    <>
      {CHAMPS_DE_LA_FEUILLE[feuille].map((champ) => {
        const valeur = lu.profil[champ];
        const crans = CRANS_DES_CHAMPS[champ];
        const ouvrirLeChamp = () => ouvrir({ sorte: 'champ', champ, feuille, valeur });
        if (crans) {
          const hors = valeurHorsDesCrans(valeur, crans);
          return (
            <Curseur
              key={champ}
              titre={MOTS_DES_CHAMPS_DU_PROFIL[champ]}
              crans={crans.map((c) => [c, c] as const)}
              choisi={valeur ?? ''}
              sansCranPossible
              attributs={{ 'data-champ': champ }}
              onChoisir={(c) => profil.corrigerUnChamp(champ, c === valeur ? '' : c)}
            >
              {hors && (
                <button type="button" className="profil-pouce-hors" aria-label={corriger(MOTS_DES_CHAMPS_DU_PROFIL[champ])} onClick={ouvrirLeChamp}>
                  {hors}
                </button>
              )}
            </Curseur>
          );
        }
        return (
          <LigneDuProfil
            key={champ}
            question={MOTS_DES_CHAMPS_DU_PROFIL[champ]}
            valeur={valeur}
            exemple={EXEMPLES_DES_CHAMPS_DU_PROFIL[champ]}
            attributs={{ 'data-champ': champ }}
            onOuvrir={ouvrirLeChamp}
          />
        );
      })}
      {lu.lignes[feuille].map((ligne) => (
        <LigneDuProfil
          key={ligne.id}
          question={ligne.intitule || INTITULE_DE_L_INFO}
          valeur={ligne.valeur || null}
          exemple={VALEUR_DE_L_INFO}
          attributs={{ 'data-ligne': ligne.id }}
          onOuvrir={() => ouvrir({ sorte: 'ligne', ligne })}
        />
      ))}
      <button type="button" className="profil-pouce-ligne profil-pouce-ajout" onClick={() => ouvrir({ sorte: 'neuve', feuille })}>
        {AJOUTER_UNE_INFO}
      </button>
    </>
  );
}

/** Une ligne de question (`.pl`) : la question, la valeur à la main ou l'exemple grisé, ›. */
function LigneDuProfil({
  question,
  valeur,
  exemple,
  attributs,
  onOuvrir,
}: {
  question: string;
  valeur: string | null;
  exemple: string;
  attributs: Record<`data-${string}`, string>;
  onOuvrir: () => void;
}) {
  return (
    <button type="button" className="profil-pouce-ligne" aria-label={corriger(question)} onClick={onOuvrir} {...attributs}>
      <span className="profil-pouce-q">{question}</span>
      <span className={`profil-pouce-v${valeur === null ? ' est-vide' : ''}`}>{valeur ?? exemple}</span>
      <span className="profil-pouce-chev" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

/** Un contact relu (`.pc`) : le nom, la bulle et son projet, le numéro avec son signe. */
function LigneDeContact({ contact }: { contact: ContactDuProfil }) {
  return (
    <div className="profil-pouce-contact" data-contact={contact.bulle.id}>
      <div>
        <b>{nomDuContact(contact)}</b>
        <small>{origineDuContact(contact)}</small>
      </div>
      {contact.telephone ? (
        <span className="profil-pouce-tel">
          <IconeCombine className="profil-pouce-combine" />
          {contact.telephone}
        </span>
      ) : (
        contact.email && <span className="profil-pouce-tel">{contact.email}</span>
      )}
    </div>
  );
}

/**
 * La feuille qui monte pour corriger (`feuilleHtml()`, branche `champ:`) : la question, le chemin,
 * la case à la main, « Claude le lit… », Enregistrer et Annuler. Une ligne libre a aussi son
 * intitulé et « Retirer cette info » ; une ligne neuve, son intitulé et sa valeur.
 */
function FeuilleDeCorrection({ ouverte, profil, onFermer }: { ouverte: FeuilleOuverte; profil: Gestes; onFermer: () => void }) {
  const depart =
    ouverte.sorte === 'champ'
      ? { intitule: '', valeur: ouverte.valeur ?? '' }
      : ouverte.sorte === 'ligne'
        ? { intitule: ouverte.ligne.intitule, valeur: ouverte.ligne.valeur }
        : { intitule: '', valeur: '' };
  const [intitule, setIntitule] = useState(depart.intitule);
  const [valeur, setValeur] = useState(depart.valeur);

  const feuille = ouverte.sorte === 'ligne' ? ouverte.ligne.feuille : ouverte.feuille;
  const titre =
    ouverte.sorte === 'champ'
      ? MOTS_DES_CHAMPS_DU_PROFIL[ouverte.champ]
      : ouverte.sorte === 'ligne'
        ? ouverte.ligne.intitule || INTITULE_DE_L_INFO
        : UNE_INFO_A_TOI;
  const exemple = ouverte.sorte === 'champ' ? EXEMPLES_DES_CHAMPS_DU_PROFIL[ouverte.champ] : VALEUR_DE_L_INFO;

  const enregistrer = (e: FormEvent) => {
    e.preventDefault();
    if (ouverte.sorte === 'champ') profil.corrigerUnChamp(ouverte.champ, valeur);
    else if (ouverte.sorte === 'ligne') {
      profil.corrigerUneLigne(ouverte.ligne, 'intitule', intitule);
      profil.corrigerUneLigne(ouverte.ligne, 'valeur', valeur);
    } else void profil.ajouterUneLigne(ouverte.feuille, intitule, valeur);
    onFermer();
  };

  let cases: ReactNode = (
    <input
      className="profil-pouce-saisie"
      value={valeur}
      placeholder={exemple}
      maxLength={BORNES_PROFIL.texteMax}
      aria-label={titre}
      autoFocus
      onChange={(e) => setValeur(e.target.value)}
    />
  );
  if (ouverte.sorte !== 'champ') {
    cases = (
      <>
        <input
          className="profil-pouce-saisie profil-pouce-saisie--intitule"
          value={intitule}
          placeholder={INTITULE_DE_L_INFO}
          maxLength={BORNES_PROFIL.texteMax}
          aria-label={INTITULE_DE_L_INFO}
          autoFocus={ouverte.sorte === 'neuve'}
          onChange={(e) => setIntitule(e.target.value)}
        />
        <input
          className="profil-pouce-saisie"
          value={valeur}
          placeholder={VALEUR_DE_L_INFO}
          maxLength={BORNES_PROFIL.texteMax}
          aria-label={VALEUR_DE_L_INFO}
          autoFocus={ouverte.sorte === 'ligne'}
          onChange={(e) => setValeur(e.target.value)}
        />
      </>
    );
  }

  return (
    <FeuilleDuBas titre={titre} onFermer={onFermer} classe="profil-pouce-correction" attributs={{ 'data-correction': ouverte.sorte }}>
      <form className="profil-pouce-form" onSubmit={enregistrer}>
        <p className="profil-pouce-chemin">{cheminDansLeProfil(TITRES_DES_FEUILLES_DU_PROFIL[feuille])}</p>
        {cases}
        <p className="profil-pouce-sous">{CLAUDE_LE_LIT}</p>
        <div className="pouce-acts">
          <button type="submit" className="est-principal">
            {ENREGISTRER_LE_PROFIL}
          </button>
          <button type="button" onClick={onFermer}>
            {ANNULER}
          </button>
        </div>
        {ouverte.sorte === 'ligne' && (
          <button
            type="button"
            className="pouce-ligne-reg pouce-ligne-reg--sortir profil-pouce-retirer"
            onClick={() => {
              profil.retirerUneLigne(ouverte.ligne);
              onFermer();
            }}
          >
            {RETIRER_L_INFO}
          </button>
        )}
      </form>
    </FeuilleDuBas>
  );
}
